import React, { useState, useEffect } from 'react';
import { BookOpen, Lock, ExternalLink, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { booksAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const Books = () => {
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchBooks();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await booksAPI.getAll();
      setBooks(response.data);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookClick = (book) => {
    setSelectedBook(book);
    setModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Books</h1>
        <p className="text-gray-400">Expand your knowledge with our curated collection of AI resources.</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
          {books.map((book) => (
            <div
              key={book.id}
              onClick={() => handleBookClick(book)}
              className="bg-[#1a1a2e] rounded-xl overflow-hidden border border-gray-800 hover:border-purple-500/50 transition-all cursor-pointer group"
            >
              <div className="relative aspect-[2/3]">
                <img
                  src={book.cover_image || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=200&h=300&fit=crop'}
                  alt={book.title}
                  className="w-full h-full object-cover"
                />
                {/* Overlay for locked books */}
                {!book.has_access && !book.is_free && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Lock size={24} className="text-yellow-500" />
                    </div>
                  </div>
                )}
                {/* Badge */}
                <div className="absolute top-2 right-2">
                  <Badge className={book.is_free ? 'bg-green-600' : 'bg-purple-600'}>
                    {book.is_free ? 'Free' : 'Premium'}
                  </Badge>
                </div>
              </div>
              <div className="p-3">
                <h3 className="text-white font-semibold text-sm line-clamp-2 group-hover:text-purple-400 transition-colors">
                  {book.title}
                </h3>
              </div>
            </div>
          ))}
        </div>
      )}

      {books.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <BookOpen size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No books available yet.</p>
        </div>
      )}

      {/* Book Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#1a1a2e] border-gray-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedBook?.title}</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex gap-4 mb-4">
              <img
                src={selectedBook?.cover_image || 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=200&h=300&fit=crop'}
                alt={selectedBook?.title}
                className="w-24 h-36 object-cover rounded-lg"
              />
              <div className="flex-1">
                <Badge className={selectedBook?.is_free ? 'bg-green-600' : 'bg-purple-600'}>
                  {selectedBook?.is_free ? 'Free' : 'Premium'}
                </Badge>
                <p className="text-gray-400 mt-3 text-sm">{selectedBook?.description}</p>
              </div>
            </div>

            {selectedBook?.is_free ? (
              /* Free book - Show PDF link */
              <div className="space-y-3">
                {selectedBook?.pdf_url && (
                  <a
                    href={selectedBook.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full bg-green-600 hover:bg-green-700">
                      <FileText size={18} className="mr-2" />
                      Read PDF
                      <ExternalLink size={14} className="ml-2" />
                    </Button>
                  </a>
                )}
              </div>
            ) : selectedBook?.has_access ? (
              /* Paid book with access - Show Drive link */
              <div className="space-y-3">
                {selectedBook?.drive_link && (
                  <a
                    href={selectedBook.drive_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <Button className="w-full bg-purple-600 hover:bg-purple-700">
                      <BookOpen size={18} className="mr-2" />
                      Open in Google Drive
                      <ExternalLink size={14} className="ml-2" />
                    </Button>
                  </a>
                )}
              </div>
            ) : (
              /* Paid book without access - Show upgrade message */
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  <Lock size={20} className="text-yellow-500" />
                  <span className="text-yellow-400 font-medium">Premium Content</span>
                </div>
                <p className="text-gray-400 text-sm mb-4">
                  {selectedBook?.required_package 
                    ? `This book requires the ${selectedBook.required_package} package.`
                    : 'Upgrade to a paid subscription to access this book.'}
                </p>
                <Link to="/credits" onClick={() => setModalOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600">
                    Upgrade Now
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Books;
